// The versioned row store behind every live list (Holders, Top traders,
// Portfolio holdings). Pure: the hooks (lib/liveRows, hooks/useWalletPortfolio)
// hold the state; this decides what one server fact does to the rows on screen.
//
// Operator rule, 2026-10-26 / 2026-11-02: every figure moves the instant the
// chain settles it, over WS; no flicker (a row never disappears and comes
// back); a holder who sells out leaves in the same instant; a reorder happens
// in place. So:
//
//   - A row carries a version (the server's monotonic marker for that row's
//     figures, e.g. `quantity_version`). A fact is taken only when it is newer
//     than what the screen shows; an older or equal one is ignored. A REST
//     re-read is just another fact: it can never roll back a pushed row.
//   - A patch with no row removes it (sell-out) and leaves a tombstone at its
//     version, so a re-read taken before the sale cannot bring it back.
//   - Every row the change does not touch keeps its object (React renders
//     nothing for it; keyed rows stay mounted through a reorder), and when
//     nothing changed at all the very same array comes back.
//
// A row with no version (an older read) is not guarded: the snapshot is the
// truth for it, as before.

/** A server version: a decimal string (lt / sequence, exact as bigint) or an integer. */
export type RowVersion = string | number | bigint

/** The version as an exact bigint; null when absent or unreadable. */
export function versionValue(v: unknown): bigint | null {
  try {
    if (typeof v === 'bigint') return v >= 0n ? v : null
    if (typeof v === 'string' && /^\d+$/.test(v)) return BigInt(v)
    if (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0) return BigInt(v)
  } catch {
    // fall through
  }
  return null
}

/** Whether version `a` is strictly newer than `b` (an absent `b` is older than any `a`). */
export function newerVersion(a: unknown, b: unknown): boolean {
  const x = versionValue(a)
  if (x == null) return false
  const y = versionValue(b)
  return y == null || x > y
}

/** One server fact about one row: the row as it now stands, or null = it left. */
export type RowPatch<R> = { key: string; version: RowVersion; row: R | null }

export type RowStoreSpec<R> = {
  keyOf: (row: R) => string
  /** the row's version; null = an unversioned row (not guarded) */
  versionOf: (row: R) => RowVersion | null | undefined
  /** the display order (returns the same array when already in order) */
  order: (rows: R[]) => R[]
  /** rows shown at most (a list the server caps); none = no cap */
  limit?: number
}

/** Rows that left, by key, with the version they left at. */
export type Tombstones = Map<string, bigint>

function sameList<R>(a: readonly R[], b: readonly R[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/** Ordered and capped; the previous array itself when nothing moved. */
function finish<R>(prev: readonly R[], next: R[], spec: RowStoreSpec<R>): R[] {
  let out = spec.order(next)
  if (spec.limit != null && out.length > spec.limit) out = out.slice(0, spec.limit)
  return sameList(prev, out) ? (prev as R[]) : out
}

/**
 * Server facts onto the rows on screen. A fact not newer than the row shown
 * (or than the key's tombstone) is ignored; a null row removes the row and
 * leaves a tombstone; a new key enters. `tombstones` is updated in place.
 */
export function applyPatches<R>(
  rows: R[],
  patches: readonly RowPatch<R>[],
  spec: RowStoreSpec<R>,
  tombstones: Tombstones
): R[] {
  if (patches.length === 0) return rows
  // insertion-ordered: a replaced row keeps its slot, a new one goes last
  const byKey = new Map<string, R>()
  for (const r of rows) byKey.set(spec.keyOf(r), r)
  let changed = false
  for (const p of patches) {
    if (patchOne(byKey, p, spec, tombstones)) changed = true
  }
  return changed ? finish(rows, [...byKey.values()], spec) : rows
}

/** One fact on the keyed rows; whether it changed them. */
function patchOne<R>(
  byKey: Map<string, R>,
  p: RowPatch<R>,
  spec: RowStoreSpec<R>,
  tombstones: Tombstones
): boolean {
  const v = versionValue(p.version)
  if (v == null) return false
  const gone = tombstones.get(p.key)
  if (gone != null && v <= gone) return false // it left at this version or later
  const shown = byKey.get(p.key)
  if (shown !== undefined && !newerVersion(v, spec.versionOf(shown))) return false // stale
  if (p.row == null) {
    tombstones.set(p.key, v)
    return byKey.delete(p.key)
  }
  tombstones.delete(p.key)
  byKey.set(p.key, p.row)
  return true
}

/** How a re-read meets the rows on screen. */
export type SnapshotGuard<R> = {
  keyOf: (row: R) => string
  versionOf: (row: R) => RowVersion | null | undefined
  /** the read's row with the figures the screen holds at a newer version
      kept (a pushed balance the read predates); default: the screen's row */
  keepNewer?: (server: R, shown: R) => R
  /** a row on screen the read does not list that stays anyway (a pushed
      newcomer the read predates); default: none */
  holdUnlisted?: (shown: R) => boolean
}

/**
 * A full re-read (REST answer) against the rows on screen, unordered:
 *  - a row the screen holds at a newer version than the read keeps the
 *    screen's versioned figures (`keepNewer`);
 *  - a read row not newer than its key's tombstone stays gone (no flicker back);
 *  - a row on screen the read does not list goes, unless `holdUnlisted`.
 * Rows of an older server (no version) are taken as read.
 */
export function guardSnapshot<R>(
  server: readonly R[],
  onScreen: readonly R[] | null,
  guard: SnapshotGuard<R>,
  tombstones: Tombstones
): R[] {
  const shown = new Map<string, R>()
  for (const r of onScreen ?? []) shown.set(guard.keyOf(r), r)
  const listed = new Set<string>()
  const out: R[] = []
  for (const r of server) {
    const key = guard.keyOf(r)
    listed.add(key)
    const row = guardRow(r, shown.get(key), key, guard, tombstones)
    if (row !== null) out.push(row)
  }
  const hold = guard.holdUnlisted
  if (hold) {
    for (const [key, s] of shown) if (!listed.has(key) && hold(s)) out.push(s)
  }
  return out
}

/** One read row against the screen's; null = it stays gone (tombstone). */
function guardRow<R>(
  r: R,
  s: R | undefined,
  key: string,
  guard: SnapshotGuard<R>,
  tombstones: Tombstones
): R | null {
  const v = guard.versionOf(r)
  const gone = tombstones.get(key)
  if (gone != null) {
    const rv = versionValue(v)
    if (rv == null || rv <= gone) return null // the read predates the sale
    tombstones.delete(key)
  }
  if (s === undefined || !newerVersion(guard.versionOf(s), v)) return r
  return guard.keepNewer ? guard.keepNewer(r, s) : s // the screen is newer
}

/** guardSnapshot, ordered and capped; an unchanged row keeps its object
    (`same`), and the very same array comes back when nothing moved. */
export function applySnapshot<R>(
  onScreen: readonly R[] | null,
  server: readonly R[],
  spec: RowStoreSpec<R> & SnapshotGuard<R> & { same: (a: R, b: R) => boolean },
  tombstones: Tombstones
): R[] {
  const shown = new Map<string, R>()
  for (const r of onScreen ?? []) shown.set(spec.keyOf(r), r)
  const guarded = guardSnapshot(server, onScreen, spec, tombstones).map((r) => {
    const s = shown.get(spec.keyOf(r))
    return s !== undefined && s !== r && spec.same(s, r) ? s : r
  })
  return finish(onScreen ?? [], guarded, spec)
}
